// @ts-nocheck

/**
 * MSI3 Facility Partner Service
 * 
 * Manages facility partnerships, QR code generation, and partnership status.
 */

import { db } from '../firebase';
import firebase from 'firebase/compat/app';
import { MSI3_CONSTANTS } from './constants';

const { PARTNERSHIP_STATUS } = MSI3_CONSTANTS;

export const facilityPartnerService = {
  /**
   * Create a new facility partnership
   * @param {Object} partnershipData - Partnership details
   * @returns {Promise<Object>} Created partnership
   */
  async createPartnership(partnershipData) {
    const {
      facilityUserId, // refs users/{uid} where role='facility'
      cityId,
      tier = null, // 1, 2, or 3 (can be null if unknown)
      bedCount = null,
      facilityType = null,
      contactEmail = null,
      contactPhone = null,
    } = partnershipData;

    // Generate unique signup code
    const uniqueSignupCode = await this.generateUniqueCode(facilityUserId);

    // Check for early adopter position
    const earlyAdopterPosition = await this.checkEarlyAdopterPosition(cityId, tier);

    const partnerRef = db.collection('msi3FacilityPartners').doc();
    const partnership = {
      id: partnerRef.id,
      facilityUserId,
      cityId,
      tier,
      bedCount,
      facilityType,
      contactEmail,
      contactPhone,
      uniqueSignupCode,
      earlyAdopterPosition,
      earlyAdopterProtectionMonths: earlyAdopterPosition
        ? MSI3_CONSTANTS.EARLY_ADOPTER_PROTECTION_MONTHS
        : MSI3_CONSTANTS.STANDARD_PROTECTION_MONTHS,
      partnershipStatus: PARTNERSHIP_STATUS.PROSPECT,
      partnershipSignedDate: null,
      paymentDelayDays: null, // Set when partnership is signed
      paymentDelayLockedAt: null,
      marketPenetrationAtPartnership: null,
      qrCodeGeneratedAt: firebase.firestore.FieldValue.serverTimestamp(),
      logoUrl: null,
      brandColorPrimary: null,
      brandColorSecondary: null,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };

    await partnerRef.set(partnership);
    return partnership;
  },

  /**
   * Generate a unique signup code for a facility
   */
  async generateUniqueCode(facilityUserId) {
    // Get facility user data for name
    const userDoc = await db.collection('users').doc(facilityUserId).get();
    const userData = userDoc.data() || {};
    const facilityName = userData.name || userData.facilityName || 'facility';

    // Create slug from facility name
    const facilitySlug = facilityName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .substring(0, 20);

    // Add random suffix for uniqueness
    const randomSuffix = Math.random().toString(36).substring(2, 8);

    const code = `${facilitySlug}_${randomSuffix}`;

    // Verify uniqueness
    const existing = await db
      .collection('msi3FacilityPartners')
      .where('uniqueSignupCode', '==', code)
      .get();

    if (!existing.empty) {
      // Retry with different random suffix
      return this.generateUniqueCode(facilityUserId);
    }

    return code;
  },

  /**
   * Check and claim early adopter position for a facility
   */
  async checkEarlyAdopterPosition(cityId, tier) {
    if (!tier) return null;

    // Count existing early adopters for this tier in this city
    const existingSnapshot = await db
      .collection('msi3FacilityPartners')
      .where('cityId', '==', cityId)
      .where('tier', '==', tier)
      .where('earlyAdopterPosition', '!=', null)
      .get();

    // Maximum early adopters per tier: Tier 1 = 2, Tier 2 = 3, Tier 3 = unlimited
    const maxEarlyAdopters = tier === 1 ? 2 : tier === 2 ? 3 : null;

    if (maxEarlyAdopters && existingSnapshot.size >= maxEarlyAdopters) {
      return null; // No early adopter positions available
    }

    return existingSnapshot.size + 1; // Next position
  },

  /**
   * Activate partnership (when contract is signed)
   */
  async activatePartnership(partnershipId, marketPenetration) {
    // Determine payment delay based on current market penetration
    let paymentDelayDays;
    if (marketPenetration <= 33) {
      paymentDelayDays = 30;
    } else if (marketPenetration <= 66) {
      paymentDelayDays = 60;
    } else {
      paymentDelayDays = 90;
    }

    await db.collection('msi3FacilityPartners').doc(partnershipId).update({
      partnershipStatus: PARTNERSHIP_STATUS.ACTIVE,
      partnershipSignedDate: firebase.firestore.FieldValue.serverTimestamp(),
      paymentDelayDays,
      paymentDelayLockedAt: firebase.firestore.FieldValue.serverTimestamp(),
      marketPenetrationAtPartnership: marketPenetration,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  },

  /**
   * Get partnership by facility user ID
   */
  async getPartnershipByFacilityUser(facilityUserId) {
    const snapshot = await db
      .collection('msi3FacilityPartners')
      .where('facilityUserId', '==', facilityUserId)
      .limit(1)
      .get();

    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  },

  /**
   * Get partnership by unique signup code
   */
  async getPartnershipByCode(uniqueSignupCode) {
    const snapshot = await db
      .collection('msi3FacilityPartners')
      .where('uniqueSignupCode', '==', uniqueSignupCode)
      .limit(1)
      .get();

    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  },

  /**
   * Get all partnerships for a city
   */
  async getPartnershipsForCity(cityId) {
    const snapshot = await db
      .collection('msi3FacilityPartners')
      .where('cityId', '==', cityId)
      .get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  },

  /**
   * Get enrollment statistics for a facility partnership
   */
  async getPartnershipStats(partnershipId) {
    const partnership = await db.collection('msi3FacilityPartners').doc(partnershipId).get();
    if (!partnership.exists) return null;

    const partnerData = partnership.data();

    // Get all enrollments for this facility
    const enrollmentsSnapshot = await db
      .collection('msi3PatientEnrollments')
      .where('enrollingFacilityId', '==', partnershipId)
      .get();

    const enrollments = enrollmentsSnapshot.docs.map((doc) => doc.data());

    // Calculate statistics
    const stats = {
      totalPatients: enrollments.length,
      inMarketPatients: enrollments.filter((e) => e.countsTowardMarketPenetration).length,
      outOfMarketPatients: enrollments.filter((e) => !e.countsTowardMarketPenetration).length,
      tierBreakdown: {
        free: enrollments.filter((e) => e.subscriptionTier === 'free').length,
        small: enrollments.filter((e) => e.subscriptionTier === 'small').length,
        medium: enrollments.filter((e) => e.subscriptionTier === 'medium').length,
        large: enrollments.filter((e) => e.subscriptionTier === 'large').length,
      },
      activeRevenueShare: enrollments.filter(
        (e) => e.revenueShareStatus === 'active' && e.currentRevenueFacilityId === partnershipId
      ).length,
      monthlyRevenue: enrollments
        .filter((e) => e.revenueShareStatus === 'active' && e.currentRevenueFacilityId === partnershipId)
        .reduce((sum, e) => sum + (e.lockedMonthlyAmount || 0), 0),
    };

    return {
      partnership: partnerData,
      stats,
    };
  },

  /**
   * Update partnership branding
   */
  async updateBranding(partnershipId, brandingData) {
    const { logoUrl, brandColorPrimary, brandColorSecondary } = brandingData;

    await db.collection('msi3FacilityPartners').doc(partnershipId).update({
      logoUrl,
      brandColorPrimary,
      brandColorSecondary,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  },

  /**
   * Generate QR code URL for facility signup
   */
  getSignupUrl(uniqueSignupCode) {
    // Use environment variable or default to production URL
    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://healthdesk.app';
    return `${baseUrl}/signup?facility=${uniqueSignupCode}`;
  },
};

